import { choose } from "../utils";
import { createTemporaryId } from "../utils/model";
import { ProjectCalendarOrderConfig } from "./ProjectCalendarOrderConfig";
import { ProjectLocalization } from "./ProjectLocalization";
import UserPreferences from "./UserPreferences";

export class Project {
  readonly id: string = createTemporaryId();
  /**
   * Name of a FontAwesome v5 icon.
   */
  readonly iconName: string = choose(["trophy", "clipboard", "bolt"]);
  readonly name: string = "";
  readonly description?: string;
  readonly teamIds: string[] = [];
  readonly localization?: ProjectLocalization;
  readonly calendarOrderConfig?: ProjectCalendarOrderConfig;
  readonly leadListVoicemailHangUpThresholdId?: number | null = 1;
  readonly leadListPredictiveWaitForEvaluation?: boolean;
  readonly themeId?: string;

  /**
   * Default color for calendar events in this project.
   */
  readonly defaultCalendarEventColor?: string;

  /**
   * Automatically selected products when creating a order manually.
   */
  readonly defaultOrderProductIds: string[] = [];

  readonly defaultUserPreferences?: Partial<UserPreferences>;
  readonly isDefault: boolean = false;

  constructor(deriveFrom?: Partial<Project>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.calendarOrderConfig) {
      this.calendarOrderConfig = new ProjectCalendarOrderConfig(this.calendarOrderConfig);
    }

    if (this.localization) {
      this.localization = new ProjectLocalization(this.localization);
    }

    if (this.themeId === 'default') {
      //  Same thing, more convenient for UI as we do not need to handle "default". 
      this.themeId = null;
    }
  }

  hasAnyLocalizationString() {
    return Boolean(
      this.localization?.order.find(string => string)
    );
  }
}

/**
 * Whether `object` is inclusive in `projectId`.
 * 
 * []    => no project
 * 
 * null  => any / all projects
 * 
 * [...] => specific projects.
 */
export function isIncludedInProject(object: { projectIds?: string[] }, projectId: string | null | undefined) {
  if (!projectId) {
    return !object?.projectIds || object?.projectIds?.length === 0;
  }

  return !object?.projectIds || object.projectIds.includes(projectId);
}